import {
  mergeResolvedLocationIds,
  normalizeResolvedLocationIds,
  resolveLocationIdsFromLocationString,
  type ResolvedLocationIds,
} from "@/lib/locationIds";
import type { Coordinates } from "@/lib/types";

export type ListingLocationResolution =
  | { ok: true; ids: ResolvedLocationIds }
  | { ok: false; error: string };

/** Profile location payload for one-tap fill on sell/edit location fields. */
export type SellProfileLocation = {
  location: string;
  coordinates?: Coordinates | null;
  placeId?: string;
  locationIds?: ResolvedLocationIds | null;
};

const LISTING_LOCATION_ID_ERROR =
  "Please select a state and LGA from the location picker so we can match nearby buyers.";

/**
 * Resolve canonical listing location IDs for near-me matching.
 * Prefers structured picker ids; falls back to parsing the location string.
 */
export function resolveSellListingLocationIds(
  location: string | null | undefined,
  pickerIds?: ResolvedLocationIds | null,
): ListingLocationResolution {
  const trimmed = location?.trim();
  if (!trimmed) {
    return { ok: false, error: "Location is required" };
  }

  const ids = mergeResolvedLocationIds(pickerIds ?? undefined, trimmed);
  if (!ids.locationStateId || !ids.locationLgaId) {
    return { ok: false, error: LISTING_LOCATION_ID_ERROR };
  }

  return { ok: true, ids };
}

/** Build structured location ids from a listing row (edit flow hydration). */
export function locationIdsFromListingRow(row: {
  location_state_id?: string | null;
  location_lga_id?: string | null;
  location_neighborhood_id?: string | null;
}): ResolvedLocationIds {
  return {
    locationStateId: row.location_state_id ?? null,
    locationLgaId: row.location_lga_id ?? null,
    locationNeighborhoodId: row.location_neighborhood_id ?? null,
  };
}

export function sellProfileLocationFromUser(
  user:
    | {
        location?: string | null;
        coordinates?: Coordinates | null;
        place_id?: string | null;
        location_state_id?: string | null;
        location_lga_id?: string | null;
        location_neighborhood_id?: string | null;
      }
    | null
    | undefined,
): SellProfileLocation | null {
  if (!user) return null;
  const location = user.location?.trim();
  if (!location) return null;

  return {
    location,
    coordinates: user.coordinates ?? null,
    placeId: user.place_id ?? undefined,
    locationIds: normalizeResolvedLocationIds(
      locationIdsFromListingRow(user),
      location,
    ),
  };
}

export function locationStringsMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

/** Args for HierarchicalLocationOnChange when applying profile location. */
export function resolveProfileLocationApplyArgs(profile: SellProfileLocation): {
  value: string;
  coordinates?: Coordinates;
  placeId?: string;
  locationIds: ResolvedLocationIds;
} {
  const value = profile.location.trim();
  const locationIds = mergeResolvedLocationIds(
    profile.locationIds ?? undefined,
    value,
  );
  const resolved =
    locationIds.locationStateId ||
    locationIds.locationLgaId ||
    locationIds.locationNeighborhoodId
      ? locationIds
      : resolveLocationIdsFromLocationString(value);

  return {
    value,
    coordinates: profile.coordinates ?? undefined,
    placeId: profile.placeId,
    locationIds: resolved,
  };
}
